import { useEffect, useState } from "react";
import { Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loading, ErrorBox } from "@/components/common";
import { selectCls } from "@/pages/Take5New";

// Matrix families (rows) x sites (columns). Only hidden pairs are stored; default = every active family applies.
export const SiteFamiliesMatrix = () => {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState({});
  const [dirty, setDirty] = useState(new Set());
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [copy, setCopy] = useState({ from: "", to: "" });
  const apply = (d) => { setData(d); setHidden(Object.fromEntries(d.sites.map((s) => [s.id, new Set(d.hidden[s.id] || [])]))); setDirty(new Set()); };
  const load = () => api.get("/admin/site-families").then((r) => apply(r.data)).catch((e) => setError(errMessage(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  const setSite = (sid, set) => {
    setHidden((h) => ({ ...h, [sid]: set }));
    const same = [...set].sort().join(",") === [...(data.hidden[sid] || [])].sort().join(",");
    setDirty((d) => { const n = new Set(d); same ? n.delete(sid) : n.add(sid); return n; });
  };
  const toggle = (sid, fid) => { const s = new Set(hidden[sid]); s.has(fid) ? s.delete(fid) : s.add(fid); setSite(sid, s); };
  const all = data ? data.families.map((f) => f.id) : [];
  const save = async (sites = [...dirty]) => {
    setBusy(true);
    try {
      let last;
      // sites sharing the same configuration are saved in one request
      const groups = {};
      sites.forEach((sid) => { const k = [...hidden[sid]].sort().join(","); (groups[k] ||= []).push(sid); });
      for (const [k, ids] of Object.entries(groups)) last = await api.put("/admin/site-families", { site_ids: ids, hidden_family_ids: k ? k.split(",") : [] });
      if (last) apply(last.data);
      toast.success("Applicabilité par site enregistrée.");
    } catch (e) { toast.error(errMessage(e)); } finally { setBusy(false); }
  };
  const doCopy = () => {
    const targets = copy.to === "*" ? data.sites.map((s) => s.id).filter((id) => id !== copy.from) : [copy.to];
    targets.forEach((t) => setSite(t, new Set(hidden[copy.from])));
    toast.message("Configuration copiée : pensez à enregistrer.");
  };
  if (error) return <ErrorBox message={error} onRetry={load} />;
  if (!data) return <Loading />;
  if (!data.sites.length) return <p className="text-sm text-muted-foreground">Ajoutez d'abord un site.</p>;
  return (
    <div data-testid="site-families-matrix">
      <p className="mb-4 text-sm text-muted-foreground">Case cochée = famille proposée dans le Take 5 sur ce site. Un nouveau site propose toutes les familles actives.</p>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-background/60">
              <th className="sticky left-0 z-10 bg-card p-3 text-left font-semibold">Famille</th>
              {data.sites.map((s) => (
                <th key={s.id} className="min-w-[140px] p-3 text-center align-top font-semibold" data-testid={`sf-site-${s.id}`}>
                  <p className="truncate">{s.name}</p>
                  <div className="mt-1 flex flex-wrap justify-center gap-1 text-xs font-normal">
                    <button type="button" data-testid={`sf-all-${s.id}`} onClick={() => setSite(s.id, new Set())} className="rounded border border-border px-2 py-1 hover:border-amber-500">Tout activer</button>
                    <button type="button" data-testid={`sf-none-${s.id}`} onClick={() => setSite(s.id, new Set(all))} className="rounded border border-border px-2 py-1 hover:border-amber-500">Tout désactiver</button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.families.map((f) => (
              <tr key={f.id} className="border-b border-border last:border-0">
                <td className="sticky left-0 z-10 bg-card p-3 font-medium">{f.name}{f.scope === "local" && <span className="ml-2 text-xs text-amber-500">Propre</span>}</td>
                {data.sites.map((s) => (
                  <td key={s.id} className="p-3 text-center">
                    <input type="checkbox" aria-label={`${f.name} sur ${s.name}`} data-testid={`sf-cell-${s.id}-${f.id}`} className="h-5 w-5 accent-amber-500"
                      checked={!hidden[s.id]?.has(f.id)} onChange={() => toggle(s.id, f.id)} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex flex-col gap-2 rounded-lg border border-border p-3 sm:flex-row sm:items-center">
        <span className="text-sm font-semibold">Copier la configuration de</span>
        <select data-testid="sf-copy-from" className={`${selectCls} h-10 sm:w-52`} value={copy.from} onChange={(e) => setCopy({ ...copy, from: e.target.value })}>
          <option value="">— site source —</option>{data.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <span className="text-sm">vers</span>
        <select data-testid="sf-copy-to" className={`${selectCls} h-10 sm:w-52`} value={copy.to} onChange={(e) => setCopy({ ...copy, to: e.target.value })}>
          <option value="">— site cible —</option><option value="*">Tous les autres sites</option>
          {data.sites.filter((s) => s.id !== copy.from).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <Button variant="outline" size="sm" data-testid="sf-copy" disabled={!copy.from || !copy.to} onClick={doCopy}>Copier</Button>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button data-testid="sf-save" disabled={busy || !dirty.size} onClick={() => save()}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Enregistrer{dirty.size ? ` (${dirty.size} site(s))` : ""}</Button>
        <Button variant="outline" data-testid="sf-reset" disabled={busy} onClick={() => { data.sites.forEach((s) => setSite(s.id, new Set())); }}><RotateCcw className="mr-1 h-4 w-4" /> Réinitialiser (toutes les familles partout)</Button>
      </div>
    </div>
  );
};
